import { Fragment, isValidElement, ReactElement, ReactNode } from 'react';
import { SelectBoxItem } from '@/components/select/selectBoxTypes';
import { Label, ListboxButton, ListboxOption, ListboxOptions, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';

interface SelectBoxFieldProps<T extends SelectBoxItem> {
  label?: string | ReactElement;
  hideLabel?: true;
  deselectLabel?: string;
  displayText: ReactNode;
  itemRender?: (item: T, selected?: boolean) => ReactElement;
  items: T[];
  allowEmptySelection?: true;
  requireSelection?: true;
  hasNoSelection: boolean;
  onDeselect: () => void;
}

const getDeselectOptionClassName = (focus: boolean, hasLabel: boolean) =>
  [
    focus ? 'text-white bg-blue-600' : 'text-gray-900',
    'cursor-pointer select-none relative pl-10 pr-4',
    hasLabel ? 'py-2' : 'py-4',
  ]
    .join(' ')
    .trim();

const getItemOptionClassName = (focus: boolean, disabled: boolean, hasName: boolean) =>
  [
    disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
    focus && !disabled ? 'text-white bg-blue-600' : 'text-gray-900',
    'select-none relative pl-10 pr-4',
    hasName ? 'py-2' : 'py-4',
  ]
    .join(' ')
    .trim();

const getCheckIconClassName = (focus: boolean) =>
  [focus ? 'text-white' : 'text-gray-900', 'absolute inset-y-0 left-0 flex items-center pl-3']
    .join(' ')
    .trim();

const SelectBoxField = <T extends SelectBoxItem>({
  label,
  hideLabel,
  deselectLabel,
  displayText,
  itemRender,
  items,
  allowEmptySelection,
  requireSelection,
  hasNoSelection,
  onDeselect,
}: SelectBoxFieldProps<T>) => (
  <>
    {!hideLabel && (
      <Label className={label && isValidElement(label) ? '' : 'block mb-1'}>{label}</Label>
    )}
    <div className="relative">
      <ListboxButton className="relative w-full cursor-default border rounded-lg bg-white py-2 pl-3 pr-10 text-left shadow focus:outline-none focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50">
        <span className="block truncate">
          {displayText}
          {!itemRender ? ' ' : ''}
        </span>
        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
          <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
        </span>
      </ListboxButton>

      <Transition
        as={Fragment}
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <ListboxOptions className="absolute z-10 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
          {allowEmptySelection && !requireSelection ? (
            <ListboxOption
              key=""
              value={null}
              onClick={onDeselect}
              className={({ focus }) =>
                getDeselectOptionClassName(focus, Boolean(deselectLabel && deselectLabel !== ''))
              }
            >
              {({ focus, selected }) => (
                <>
                  {deselectLabel && deselectLabel !== '' && (
                    <span className="block truncate">{deselectLabel}</span>
                  )}
                  {selected || hasNoSelection ? (
                    <span className={getCheckIconClassName(focus)}>
                      <CheckIcon className="h-5 w-5" aria-hidden="true" />
                    </span>
                  ) : null}
                </>
              )}
            </ListboxOption>
          ) : null}
          {items.map((item, index) => (
            <ListboxOption
              key={item.id + index}
              value={item}
              disabled={item.disabled}
              className={({ focus, disabled }) =>
                getItemOptionClassName(focus, disabled, item.name !== '')
              }
            >
              {({ focus, selected, disabled }) => (
                <>
                  {itemRender ? (
                    itemRender(item, selected)
                  ) : (
                    <span
                      className={`${selected ? 'font-semibold' : 'font-normal'} block truncate`}
                    >
                      {item.name}
                    </span>
                  )}
                  {selected && !disabled ? (
                    <span className={getCheckIconClassName(focus)}>
                      <CheckIcon className="h-5 w-5" aria-hidden="true" />
                    </span>
                  ) : null}
                </>
              )}
            </ListboxOption>
          ))}
        </ListboxOptions>
      </Transition>
    </div>
  </>
);

export default SelectBoxField;
